import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, forkJoin, map, mergeMap, of, withLatestFrom } from 'rxjs';
import { AccountLinks } from '../../models/user';
import { GlobalDataService } from '../../services/faker/global-data.service';
import { FinanceService } from '../../services/finance/finance.service';
import { selectAllStudents } from '../students/student.selectors';
import { FinanceActions } from './finance.actions';
import { selectSelectedAccountLinks } from './finance.selectors';

@Injectable()
export class FinanceEffects {
  constructor(
    private actions$: Actions,
    private globalDataService: GlobalDataService,
    private financeService: FinanceService,
    private store: Store,
  ) {}

  loadDebitOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.loadDebitOrders),
      mergeMap(() => {
        const data = this.globalDataService.getDebitOrders();
        if (data && data.length > 0) {
          return of(data).pipe(
            map((debitOrders) => FinanceActions.loadDebitOrdersSuccess({ debitOrders })),
            catchError((error) => of(FinanceActions.loadDebitOrdersFailure({ error }))),
          );
        } else {
          return of(FinanceActions.loadDebitOrdersFailure({ error: 'No debit orders found' }));
        }
      }),
    ),
  );

  loadBankingDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.loadBankingDetails),
      mergeMap(({ accountLinks }) => {
        if (!accountLinks?.length) {
          return of(
            FinanceActions.loadBankingDetailsFailure({
              error: 'No linked company codes provided',
            }),
          );
        }

        // Create observables for each company code
        const bankDetailsRequests = accountLinks.map((links) => this.financeService.getBankingDetails(links));

        // Combine all requests
        return forkJoin(bankDetailsRequests).pipe(
          map((results) => {
            // Flatten the array of arrays
            const bankDetails = results.flat();
            if (bankDetails.length > 0) {
              return FinanceActions.loadBankingDetailsSuccess({ bankDetails });
            } else {
              return FinanceActions.loadBankingDetailsFailure({
                error: 'No banking details found',
              });
            }
          }),
          catchError((error) => of(FinanceActions.loadBankingDetailsFailure({ error }))),
        );
      }),
    ),
  );

  loadBankBalances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.loadBankRecords),
      withLatestFrom(this.store.select(selectAllStudents)),
      mergeMap(([{ accountLinks }, learners]) => {
        if (!accountLinks?.length) {
          return of(
            FinanceActions.loadBankRecordsFailure({
              error: 'No account links provided',
            }),
          );
        }

        const hasActiveLearner = (link: AccountLinks): boolean =>
          learners?.some((learner) => {
            const sameCompany = learner?.school?.myHubCompanySettings?.companyCode === link.companyCode;
            const sameAccount = learner?.school?.accountCode === link.accountCode;
            const isActive = learner.enrolmentStatus !== 'Inactive' && learner.enrolmentStatus !== 'Cancelled';
            return sameCompany && sameAccount && isActive;
          }) ?? false;

        const value = this.financeService.getBalancesForAccounts(accountLinks).pipe(
          map((records) =>
            records.filter((record) => {
              const isDormant = record.balance === 0 && record.active === false;
              return !isDormant || hasActiveLearner(record.accountLinks);
            }),
          ),
          map((statements) => FinanceActions.loadBankRecordsSuccess({ statements })),
          catchError((error) => of(FinanceActions.loadBankRecordsFailure({ error }))),
        );

        return value;
      }),
    ),
  );

  loadAccountHolderDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.loadAccountHolderDetails),
      withLatestFrom(this.store.select(selectSelectedAccountLinks)),
      mergeMap(([action, accountLinks]) => {
        if (!accountLinks) {
          return of(
            FinanceActions.loadAccountHolderDetailsFailure({
              error: 'No account link provided',
            }),
          );
        }

        return this.financeService.getAccountHolderDetails(accountLinks).pipe(
          map((accountHolder) => {
            if (accountHolder) {
              return FinanceActions.loadAccountHolderDetailsSuccess({ accountHolder });
            } else {
              return FinanceActions.loadAccountHolderDetailsFailure({
                error: 'No account holder details found',
              });
            }
          }),
          catchError((error) => of(FinanceActions.loadAccountHolderDetailsFailure({ error }))),
        );
      }),
    ),
  );
}
